import { inject, Injector, Service } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import type { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { waitForSignal } from '../../../shared/utils/wait-for-signal';
import { AnalysisToolComponent } from '../../analysis/containers/analysis-tool/analysis-tool.component';
import { AuthService } from '../services/auth.service';
import { Auth0Store } from './../store';

@Service()
export class LoginGuard {
  private injector = inject(Injector);
  private authService = inject(AuthService);
  private router = inject(Router);
  private _auth0Store: InstanceType<typeof Auth0Store>;

  private get auth0Store() {
    if (!this._auth0Store) {
      this._auth0Store = this.injector.get(Auth0Store);
    }
    return this._auth0Store;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.getToken().pipe(
      take(1),
      switchMap(async (token) => {
        if (!token) {
          this.auth0Store.setRedirectAfterAuth(state.url);
          route.parent?.component === AnalysisToolComponent
            ? this.router.navigate(['/analysis-tool/join-cta'])
            : this.router.navigate(['/login']);
          return false;
        }
        // Token is present — wait for userDetails. Just after sign-up the token
        // is written to localStorage synchronously, but userDetails is populated
        // by an async loadUserDetails() roundtrip; reading the signal here
        // synchronously would race and bounce the user to /login (GA-28743).
        await waitForSignal(() => this.auth0Store.userDetails(), this.injector);
        return true;
      }),
    );
  }
}
